import type { SOCIAL_NAME } from '@/src/features/auth/models/auth.enums'
import type { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'

export const TEST_ID = {
  ADMIN: {
    BACK: 'admin-back',
    CLOSE: 'admin-close',
    WEBVIEW: 'admin-webview',
  },
  MISCELLANEOUS: {
    ACTION_SHEET_MODAL: 'action-sheet-modal',
    ARTIFICIAL_NAVIGATION: 'artificial-navigation',
    AVATAR: 'avatar',
    BADGE_CHILDREN: 'badge-children',
    BADGE_CONTENT: 'badge-content',
    BULLET_CHILDREN: 'bullet-children',
    BUTTON: 'button',
    CANDLE: 'candle',
    CATEGORY_MOBILE_LIST: 'category-mobile-list',
    CODE_INPUT: 'code-input',
    COMMUNITY_IMAGE: 'community-image',
    COMMUNITY_JOIN_NEXT_PAGE_TEXT: 'community-join-next-page-text',
    COMMUNITY_RANDOM_INTENTION_PRAY_PAGE: 'community-random-intention-pray-page',
    COMMUNITY_TAGS: 'community-tags',
    CONGRATULATION_PAGE: 'congratulation-page',
    CONTAINER_STYLE: 'container-style',
    CONTENT_TEST: 'content-test',
    COUNT_1: 'count1',
    COUNT_2: 'count2',
    DESKTOP_SLIDER: 'desktop-slider',
    ERROR_BOUNDARY_WRAPPER: 'AAA',
    FLAT_LIST: 'flat-list',
    FOOTER: 'footer',
    ICON_ALERT_ITEM: 'icon-alert-item',
    ICON_CHEVRON_RIGHT: 'icon-chevron-right',
    IFRAME_ERROR: 'iframe-error',
    IFRAME_ERROR_RETRY: 'iframe-error-retry',
    IFRAME_WEBVIEW: 'iframe-webview',
    INPUT: 'input',
    INTENTION_SUBMITTER: 'intention-submitter',
    INTENTION_WHO_PRAYED: 'intention-who-prayed',
    MAIL_SCREEN_CONTAINER: 'mail-screen-container',
    MESSAGE_BUTTON: 'message-button',
    MESSAGE_CLOSE_BUTTON: 'message-close-button',
    MESSAGE_ICON: 'message-icon',
    MESSAGE_LINK: 'message-link',
    MOBILE_SLIDER: 'mobile-slider',
    OPEN_ACTION_SHEET: 'open-action-sheet',
    OPEN_MODAL_INPUT: 'open-modal-input',
    PARALLAX_FLAT_LIST: 'parallax-flat-list',
    PARALLAX_FLAT_LIST_ANIMATED_IMAGE_MOBILE: 'parallax-flat-list-animated-image-mobile',
    PARALLAX_FLAT_LIST_IMAGE_DESKTOP: 'parallax-flat-list-image-desktop',
    PARALLAX_FLAT_LIST_IMAGE_MOBILE: 'parallax-flat-list-image-mobile',
    PARALLAX_FLAT_LIST_SMALL_HEADER: 'parallax-flat-list-small-header',
    PLACEHOLDER: 'placeholder',
    PRAYER_SPACE_TEST_PAGE: 'prayer-space-test-page',
    PRAYERS_LIST_ITEM: 'prayers-list-item',
    PUBLICATION_SHARE_BUTTON: 'publication-share-button',
    RECAPTCHA_V2: 'recaptcha-v2',
    SCROLL_COUNT: 'scroll-count',
    SCROLL_VIEW: 'scroll-view',
    SPECIAL_ALERT_CLOSE_BUTTON: 'special-alert-close-button',
    SWITCH_ANIMATED_VIEW: 'switch-animated-view',
    SWITCH_PRESSABLE: 'switch-pressable',
    TAG_IMAGE: 'tag-image',
    TEXTAREA: 'textarea',
    TOGGLE_BIRTHDAY_CARD: 'toggle-birthday-card',
    TRIGGER_BUTTON: 'trigger-button',
  },
  AUTH: {
    SOCIAL_CONNECT_BUTTON: (socialName: SOCIAL_NAME) => `social-connect-button-${socialName}`,
    EMAIL: {
      INPUT: 'auth-email-input',
      SUBMIT_BUTTON: 'auth-email-submit-button',
    },
    PASSWORD: {
      INPUT: 'auth-password-input',
      SUBMIT_BUTTON: 'auth-password-submit-button',
    },
    NAME: {
      INPUT: 'auth-name-input',
      SUBMIT_BUTTON: 'auth-name-submit-button',
    },
  },
  APP: {
    LOGO: 'app-logo',
    IMAGE: 'app-image',
    DOWNLOAD_BUTTON: 'app-download-button',
    ANDROID_PLAY_STORE: 'android-play-store',
    IOS_APP_STORE: 'ios-app-store',
  },
  ACCESSIBILITY_MENU: {
    MAIN_BUTTON: 'accessibility-menu-button-to-main-content',
    MENU_BUTTON: 'accessibility-menu-button-to-menu',
    NAVIGATION_BUTTON: 'accessibility-menu-button-to-navigation',
  },
  ACTION_SHEET: {
    CANCEL_BUTTON: 'action-sheet-cancel-button',
  },
  ALERT: {
    PLACEHOLDER: 'alert-banner-placeholder',
    BANNER: 'alert-banner',
  },
  CONTENT_DETAILS: {
    CHEVRON: 'content-details-chevron',
    COLLAPSIBLE: 'content-details-collapsible',
    CONTENT: 'content-details-content',
  },
  CONVERSATION: {
    HEADER: {
      BACK: 'conversation-header-back-button',
      OPTIONS_BUTTON: 'conversation-header-options-button',
    },
    PREVIEW: {
      LIST: 'conversation-preview-list',
      ITEM: 'conversation-preview-item',
      ITEM_PLACEHOLDER: 'conversation-preview-item-placeholder',
      OPTIONS_BUTTON: 'conversation-preview-options-button',
    },
    MESSAGE: {
      LIST: 'conversation-messages-list',
      OPTION_BUTTON: 'conversation-message-option-button',
    },
  },
  DONATION: {
    CAMPAIGN_LINK: 'campaign-link',
    GRID: 'donation-grid',
    BANNER: 'donation-banner',
    PLACEHOLDER: 'donation-banner-placeholder',
    DONATE_BUTTON: 'donation-banner-donate-button',
    PROGRESS_BAR: 'donation-banner-progress-bar',
  },
  HEADER: {
    BACK_BUTTON: 'header-back-button',
    DEFAULT: 'header-default',
  },
  COMMENT: {
    LOAD_RESPONSES: 'load-responses',
    LOAD_COMMENTS: 'load-comments',
    LOAD_MORE_COMMENTS: 'load-more-comments',
    PRAY_BUTTON: (id: number) => `comment-pray-button-${id}`,
    PRAYED_BUTTON: (id: number) => `comment-prayed-button-${id}`,
    OPTIONS: {
      BUTTON: (id: number) => `intention-options-button-${id}`,
      REPORT_ABUSE: 'intention-report-abuse-button',
      DELETE: 'intention-delete-button',
    },
  },
  INTENTIONS: {
    SCREEN: {
      NAME: 'intentions-screen',
      THEME_SCREEN: 'intentions-theme-screen',
      PRAY_SCREEN: 'intentions-pray-screen',
      CREATION_REWARD_SCREEN: 'intentions-creation-reward-screen',
    },
    CARD: {
      BASE: (type: INTENTION_TYPE, id: number) => `intentions-card-${type}-${id}`,
      PRAY_SUCCESS: (type: INTENTION_TYPE, id: number) => `intentions-pray-success-${type}-${id}`,
      PRAY_BUTTON: (type: INTENTION_TYPE, id: number) => `intentions-pray-button-${type}-${id}`,
      LOADING: 'intentions-card-loading',
    },
    FORM: {
      CARD_PRESS: 'intentions-form-card-press',
      LOADING: 'intentions-form-loading',
      ERROR: 'intentions-form-error',
      PRAYERS_LIST: 'intentions-form-prayers-list',
      SCREEN: {
        NAME: 'intentions-form-screen',
        BACK: 'intentions-form-screen-back-button',
        LOADING: 'intentions-form-screen-loading',
        TEXTAREA: 'intentions-form-screen-textarea',
        DELETE: 'intentions-form-screen-delete-button',
        SEND: 'send-intention-button',
      },
    },
    PRAY: {
      SCREEN: {
        NAME: 'intentions-pray-screen',
        TITLE: 'intention-pray-title',
      },
    },
  },
  INTENTION: {
    VIEW_ALL: 'intention-view-all',
    UPLIFT: {
      CREATE: {
        TEXTAREA: 'intention-create-uplift-textarea',
      },
      ANSWER: {
        BUTTON: 'intention-options-answer',
        TEXTAREA: 'intention-uplift-answer-textarea',
      },
      TOGGLE: 'intention-uplift-toggle',
      OPTIONS: {
        BUTTON: 'intention-uplift-options-button',
        REPORT_ABUSE: 'intention-uplift-report-abuse-button',
      },

      FORM: {
        INPUT: 'intention-uplift-form-input',
        SUBMIT_BUTTON: 'intention-uplift-form-submit-button',
      },
    },
    RANDOM: {
      LOADING: 'intention-random-loading',
    },
    SHARE: {
      BUTTON: 'intention-share-button',
      COPY: 'intention-share-copy',
      EMAIL: 'intention-share-email',
      WHATSAPP: 'intention-share-whatsapp',
    },
    FORM: {
      INPUT: 'intention-form-input',
      SUBMIT_BUTTON: 'intention-form-submit-button',
    },
    SCREEN: {
      NAME: 'intention-screen',
      PRAYERS: 'intention-prayers',
      ERROR: 'intention-screen-error',
    },
    REWARD: {
      SCREEN: {
        NAME: 'intention-reward-screen',
        LOADING: 'intention-reward-screen-loading',
        ERROR: 'intention-reward-screen-error',
        RANDOM: 'intention-reward-screen-random',
      },
    },
    PRAY: {
      SCREEN: {
        NAME: 'intention-pray-screen',
        BUTTON: 'intention-pray-button',
        CANDLE: {
          FULL: 'intention-pray-candle-full',
          EMPTY: 'intention-pray-candle-empty',
        },
        ERROR: 'intention-pray-screen-error',
      },
    },
    PRAYERS: {
      SCREEN: {
        LIST: 'intention-prayers-screen-list',
        BACK_BUTTON: 'intention-prayers-screen-back-button',
      },
    },
    OPTIONS: {
      BUTTON: 'intention-options-button',
      REPORT_ABUSE: 'intention-report-abuse-button',
    },
  },
  MODAL: {
    BACKDROP: 'modal-backdrop',
    WRAPPER: 'modal-wrapper',
    CLOSE_BUTTON: 'modal-close-button',
    HANDLE: 'modal-handle',
    SHARE: 'modal-share',
    LOADING: 'modal-loading',
    CONFIRM_BUTTON: 'modal-confirm',
  },
  MENU: {
    ITEM: {
      LOGO: 'logo-menu-item',
    },
    GUIDES: {
      LIST: 'guides-list',
    },
  },
  ME: {
    AUTHENTICATION: {
      SCREEN: 'login-screen',
      USERNAME_INPUT: 'input-login-username',
      PASSWORD_INPUT: 'input-login-password',
      LOGIN_BUTTON: 'login-button-connect',
      OTP_CODE_SUBMIT: 'otp-code-submit',
      OTP_DETAILS_SUBMIT: 'otp-details-submit',
      OTP_EMAIL_INPUT: 'otp-email-input',
      OTP_NAME_INPUT: 'otp-name-input',
      OTP_RESEND_BUTTON: 'otp-resend-button',
      APPLE_LOGIN_BUTTON: 'login-button-apple',
      GOOGLE_LOGIN_BUTTON: 'login-button-google',
    },
    WIZARD: {
      SCREEN: 'me-wizard-screen',
      THEME: 'me-wizard-theme-step',
      TOPICS: 'me-wizard-topics-step',
      TOPICS_TAG_BTN: 'me-wizard-topics-tag-button',
      ABOUT_YOU: 'me-wizard-about-you-step',
      LOCATION: 'me-wizard-location-step',
      END: 'me-wizard-end-step',
      BTN_NEXT: 'me-wizard-next-button',
    },
  },
  MESSAGES: {
    SCREEN: 'messages-screen',
    SINGLE_CONVERSATION: 'messages-single-conversation',
    SUBMIT_BUTTON: 'messages-submit-button',
    TEXT_INPUT: 'messages-text-input',
    PRAY_BUTTON: 'messages-pray-button',
  },
  PRAY: {
    CANDLE: {
      FULL: 'candle-full',
      PENDING: 'candle-pending',
    },
    SCREEN: {
      CLOSE: 'pray-screen-close',
      NAME: 'pray-screen',
      TITLE: 'pray-screen-title',
      AMEN_BUTTON: 'pray-screen-amen-button',
      LOADING: 'pray-screen-loading',
      ERROR: 'pray-screen-error',
    },
  },
  PRAYER: {
    PROMPT: {
      MINI_PROMPT: 'prayer-mini-prompt',
    },
  },
  PUBLICATION: {
    HEADER: {
      OPEN_MENU: 'publication-screen-menu-button',
      BACK: 'publication-screen-back-button',
      SHARE: 'publication-screen-share-button',
    },
    OPTIONS: {
      VIEW_COMMUNITY: 'publication-view-community',
      MANAGE_CONTENT: 'publication-manage-content',
      MENU: 'publication-options-menu-button',
      NOTIFICATIONS: 'publication-notifications',
      LEAVE: 'publication-community-leave',
      FAVORITE: 'publication-favorite',
    },
    SCREEN: {
      NAME: 'publication-screen',
      LOADING: 'publication-screen-loading',
      ERROR: 'publication-screen-error',
      COMMENT_PRAY_SCREEN: 'publication-comment-pray-screen',
    },
    REWARD: {
      SCREEN: {
        INTENTIONS: {
          LOADING: 'publication-reward-screen-content-loading',
        },
        NAME: 'publication-reward-screen',
        LOADING: 'publication-reward-screen-loading',
        ERROR: 'publication-reward-screen-error',
      },
      INTENTIONS: {
        PRAY: 'publication-reward-intentions-pray-button',
      },
      SHARE_CARD: {
        DEFAULT: 'publication-reward-share-card',
        BUTTON: 'publication-reward-share-button',
        COMMUNITY: 'publication-reward-share-community',
        COMMUNITY_IMG: 'publication-reward-share-community-img',
        COMMUNITY_BUTTON: 'publication-reward-share-community-button',
        PUBLICATION: 'publication-reward-share-publication',
        PUBLICATION_BUTTON: 'publication-reward-share-publication-button',
        COMMUNITY_MODAL: 'publication-reward-share-community-modal',
      },
    },
    SHARE: {
      MODAL: 'publication-share-modal',
    },
    COMMENTS: {
      INPUT: 'publication-comment-input',
      SUBMIT_BUTTON: 'publication-comment-submit-button',
      LOAD_RESPONSES: 'load-responses',
      LOAD_COMMENTS: 'load-comments',
      LOAD_MORE_COMMENTS: 'load-more-comments',
    },
  },
  PUBLICATION_CARD: {
    SHARE_BUTTON: 'publication-card-share-button',
    COMMENT_BUTTON: 'publication-card-comment-button',
    BOOKMARK_BUTTON: 'publication-card-bookmark-button',
    OPTIONS_BUTTON: 'publication-card-options-button',
    SCHEDULED: 'publication-card-scheduled',
    THREAD_TOP: 'publication-card-thread-top',
    THREAD_BOTTOM: 'publication-card-thread-bottom',
  },
  REPORT_ABUSE: {
    INTENTION: {
      SCREEN: {
        NAME: 'report-abuse-intention-screen',
      },
    },
  },
  REWARD: {
    CONTINUE: 'reward-screen-continue-button',
    IMAGE: 'reward-screen-image',
    QUOTE: 'reward-screen-quote',
    AUTHOR: 'reward-screen-author',
  },
  NAVIGATION: {
    TAB: 'navigation-tab',
    SETTINGS_LINK: 'navigation-settings-link',
    BURGER_MENU: (suffix: string = '') =>
      'header-navigation-open-button' + suffix ? `header-navigation-open-button-${suffix}` : '',
  },
  COMMUNITY: {
    SCREEN: 'community-screen',
    PUBLICATIONS_SCREEN: 'community-publications-screen',
    VIRTUS_TODAY_PUBLICATION: 'community-virtus-today-publication',
    ADMIN_BUTTON: 'community-admin-button',
    JOIN_SCREEN: 'community-join-screen',
    JOIN_CONGRATULATION_SCREEN: 'community-join-congratulation-screen',
    JOIN_SETUP_MAILS_SCREEN: 'community-join-setup-mails-screen',
    JOIN_NEXT_SCREEN: 'community-join-next-screen',
    JOIN_NEXT_INTENTION_PRAY_SCREEN: 'community-join-next-intention-pray-screen',
    NOTIFICATION_PERMISSIONS_SCREEN: 'community-notification-permissions-screen',
    SUBSCRIPTION_CARD: 'community-subscription-card',
    SHARE: {
      MODAL: 'community-share-modal',
      BUTTON: 'community-share-button',
    },
    LEAVE: 'community-leave-button',
    PICTURE: 'community-picture',
    SHARE_CARD: 'community-share-card',
    JOIN_NEXT: {
      INTENTION: {
        PRAY: 'community-join-next-intention-pray-button',
      },
    },
    NOTIFICATIONS: {
      SUBTITLE: 'community-notifications-subtitle',
      TITLE: 'community-notifications-title',
    },
    OPTIONS: {
      BUTTON: 'community-options-button',
      ADMIN_BUTTON: 'community-admin-button',
      NOTIFICATIONS: 'community-options-notifications',
    },
    PUBLICATION_CARD: {
      PUBLISHED: 'community-publication-card-published',
      SCHEDULED: 'community-publication-card-scheduled',
    },
    PUBLICATION_ITEM: {
      PUBLISHED: 'community-publication-item-published',
      SCHEDULED: 'community-publication-item-scheduled',
    },
    JOIN: {
      CHOOSE_DATE: 'community-join-choose-date',
      ONGOING_BUTTON: 'community-join-ongoing-button',
      GO_TODAY: 'community-join-go-today',
      GO_ONGOING: 'community-join-go-ongoing',
      GO_SELECTED_DATE: 'community-join-go-selected-date',
      ENABLE_DATE_EDITION: 'community-join-enable-date-edition',
      CHANGE_DATE: 'community-join-change-date',
      BACK_BUTTON: 'community-join-back-button',
    },
  },
  PRAYER_SPACE: {
    SCREEN: 'prayer-space-screen',
    ONBOARDING_STEP: 'prayer-space-onboarding-step',
    FEED_ITEM_LIST: 'feed-item-list',
    FEED_ITEM_PLACEHOLDER: 'prayer-space-feed-item-placeholder',
    COMMUNITY_SUGGESTIONS_SECTION: 'community-suggestions-section',
    MY_COMMUNITIES_BUTTON: 'my-communities-button',
    JOURNEY_SLIDER_PLACEHOLDER: 'journey-slider-placeholder',
    JOURNEY_SLIDER_THUMBNAIL: 'journey-slider-item',
    EVERLASTING_SLIDER_PLACEHOLDER: 'everlasting-slider-placeholder',
    PAPA_QUIZ_BLOCK: 'papa-quiz-block',
    PAPA_QUIZ_CLOSE_BUTTON: 'papa-quiz-close-button',
    PAPA_QUIZ_CONFIRM_BUTTON: 'papa-quiz-confirm-button',
    PAPA_STYLE_BACKGROUND: 'papa-style-background',
  },
  ELEMENT: {
    DATETIMEPICKER: 'datetimepicker',
    STEPPER: 'element-stepper',
    STAMP_STEPPER: 'element-stamp-stepper',
    STAMP_LINK: 'element-stamp-link',
    STEP: 'element-stepper-step',
    ROSARIO_BLOC: 'element-rosario-bloc',
    WATCHMAN_THUMBNAIL: 'element-watchman-thumbnail',
  },
  SETTINGS: {
    LOADING: 'settings-loading',
    SIDEBAR: 'settings-sidebar',
    AVATAR_SCREEN: 'settings-avatar-screen',
    AVATAR_LIST_SCREEN: 'settings-avatar-list-screen',
    LAYOUT: {
      SCREEN: 'settings-layout-screen',
      BACK_BUTTON: 'settings-layout-back-button',
    },
    LOCATION: {
      INPUT: 'settings-location-input',
      MODAL: 'settings-location-modal',
      BUTTON: 'settings-location-button',
    },
    DELETE_ACCOUNT: {
      BUTTON: 'delete-account-button',
      MODAL: 'delete-account-modal',
      INPUT: 'delete-account-modal-input',
      CONFIRM: 'delete-account-modal-confirm-button',
      CANCEL: 'delete-account-modal-cancel-button',
    },
    SCREEN: 'settings-screen',
    USER_AVATAR: 'settings-user-avatar',
    USER_EDIT_BUTTON: 'settings-user-edit-button',
    CREATE_CONTENT_BUTTON: 'settings-create-content-button',
    ACCOUNT: {
      DISCONNECT: 'settings-account-disconnect-button',
      SCREEN: 'settings-account-screen',
      NAVIGATOR: 'settings-account-navigator',
    },
    COMMUNITIES: {
      SCREEN: 'settings-communities-screen',
      NAVIGATOR: 'settings-communities-navigator',
    },
    GENERAL: {
      SCREEN: 'settings-general-screen',
      ALL_EMAILS: 'settings-general-all-emails',
      ALL_PUSH: 'settings-general-all-push',
      NAVIGATOR: 'settings-general-navigator',
      NEWSLETTER: 'toggle-notification-newsletter',
      DIGEST: 'toggle-notification-digest',
      ON_INTENTION_FINISHED: 'toggle-notification-on-intention-finished',
      ON_INTENTION_WATCHMAN: 'toggle-notification-on-intention-watchman',
      ON_PRIVATE_MESSAGE: 'toggle-notification-on-private-message',
      ON_GODSON_JOINED: 'toggle-notification-on-godson-joined',
      ON_COMMUNITY_JOINED: 'toggle-notification-on-community-joined',
      ON_RETREAT_ENDED: 'toggle-notification-on-retreat-ended',
      ON_USER_CATCHUP: 'toggle-notification-on-user-catchup',
    },
    DEVICES: {
      NOTIFICATION: 'settings-devices-notification',
      SCREEN: 'settings-devices-screen',
      NAVIGATOR: 'settings-devices-navigator',
    },
    COMMUNITY_NOTIFICATIONS: {
      SCREEN: 'settings-community-notifications-screen',
      PLACEHOLDER: 'settings-community-notifications-placeholder',
    },
    COMPONENTS: {
      CARD_GENERAL_SWITCH_INPUT: {
        MAIL: 'card-general-switch-input-mail',
        PUSH: 'card-general-switch-input-push',
      },
    },
    LANGUAGE: {
      SELECT: 'settings-language-select',
      INPUT: 'settings-language-input',
      MODAL: 'settings-language-modal',
      CONFIRM: 'settings-language-confirm-button',
    },
    BIRTHDATE: {
      PICKER: 'ACCOUNT_BIRTHDATE_PICKER',
      MODAL: 'ACCOUNT_BIRTHDATE_MODAL',
      INPUT: 'ACCOUNT_BIRTHDATE_INPUT',
      CANCEL: 'ACCOUNT_BIRTHDATE_CANCEL',
      CONFIRM: 'ACCOUNT_BIRTHDATE_CONFIRM',
    },
  },
  COMMUNITIES: {
    SCREEN: 'communities-screen',
    CATEGORY_SCREEN: 'communities-category-screen',
    MOBILE_SEARCH_SCREEN: 'communities-mobile-search-screen',
    CATEGORIES: 'communities-categories',
    FEATURED_TAG: 'communities-featured-tag',
    CATEGORIES_MOBILE_LOADING: 'communities-category-mobile-loading',
    SUGGESTED_COMMUNITIES: 'communities-suggested-communities',
    AT_THE_MOMENT_COMMUNITIES: 'communities-at-the-moment-communities',
    FAVORITE_COMMUNITIES: 'communities-favorite-communities',
    ALL_CATEGORIES: 'communities-all-categories',
    SEARCH_INPUT: 'communities-search-input',
    SEARCH_PLACEHOLDER: 'search-placeholder',
    INPUT_BUTTON: 'communities-input-button',
    SEARCH_INPUT_MOBILE: 'communities-search-input-mobile',
    SEARCH_INPUT_CROSS: 'communities-search-input-cross',
  },
  AVATAR: {
    ACCOUNT_BUTTON: 'avatar-account-button',
    CHOOSE_BUTTON: 'avatar-selection',
  },
  PROFILE: {
    COMPLETED_SUBSCRIPTIONS_SCREEN: 'profile-completed-subscriptions-screen',
    CREATED_COMMUNITIES: 'profile-created-communities',
    DONE: {
      LOADING: 'profile-done-loading',
      COMMUNITY_CARDS: 'done-community-cards',
    },
  },
  LEAVE: {
    SCREEN: 'leave-screen',
  },
  CONTACT: {
    SCREEN: 'contact-screen',
    EMAIL_INPUT: 'contact-email-input',
    FIRSTNAME_INPUT: 'contact-firstname-input',
    LASTNAME_INPUT: 'contact-lastname-input',
    SUBJECT_INPUT: 'contact-subject-input',
    MESSAGE_INPUT: 'contact-message-input',
    SEND_BUTTON: 'contact-send-button',
    CAPTCHA_V2_MODAL: 'contact-captcha-v2-modal',
  },
  LAYOUT: {
    COMMON: 'common-layout',
    ERROR_BOUNDARY: 'common-layout-error-boundary',
  },
  PAGE: {
    IFRAME: 'page-screen-iframe',
  },
  WATCHMAN: {
    INTENTIONS: {
      SCREEN: 'watchman-intentions-screen',
      PRAY_BUTTON: 'watchman-intentions-pray-button',
      AMEN_BUTTON: 'watchman-intentions-amen-button',
      SEND_BUTTON: 'watchman-intentions-send-button',
      CONTINUE_BUTTON: 'watchman-intentions-continue-button',
      STREAK: (level: number) => `watchman-streak-${level}`,
      STREAK_FLAME: 'watchman-streak-flame',
      STREAK_CARD: 'watchman-streak-card',
    },
    LOADING: 'watchman-loading',
    SECTION_BUTTON: 'watchman-section-button',
    SCREEN: {
      NAME: 'watchman-screen',
      ASK_LATER: 'watchman-ask-later',
      BECOME_WATCHMAN: 'watchman-become-watchman',
    },
    REWARD: {
      SCREEN: {
        NAME: 'watchman-reward-screen',
      },
    },
    SUMMARY: {
      DEFAULT: 'watchman-summary',
      PLACEHOLDER: 'watchman-summary-placeholder',
      BTN_MENU: 'watchman-summary-btn-menu',
      REFRESH_BTN: 'watchman-summary-refresh-button',
    },
  },
} as const
